import { useEffect, useState } from "react";
import { api, API, fmtMoney } from "@/lib/api";
import { Card, CardHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { DownloadSimple, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const METHODS = [
  { value: "fifo", label: "FIFO", desc: "First-in, first-out (IRS default)" },
  { value: "lifo", label: "LIFO", desc: "Last-in, first-out" },
  { value: "hifo", label: "HIFO", desc: "Highest-cost first (minimises gains)" },
];

export default function HistoryPage() {
  const [trades, setTrades] = useState([]);
  const [method, setMethod] = useState(() => localStorage.getItem("stratos.cost_basis") || "fifo");
  useEffect(() => {
    api.get("/trades").then(r => setTrades(r.data.trades || [])).catch(() => {});
  }, []);

  const download = async (chosenMethod = method) => {
    try {
      const res = await api.get(`/trades/export?method=${chosenMethod}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stratos_trades_${chosenMethod}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setMethod(chosenMethod);
      localStorage.setItem("stratos.cost_basis", chosenMethod);
      toast.success(`Exported ${trades.length} trades (${chosenMethod.toUpperCase()})`);
    } catch (e) { toast.error("Export failed"); }
  };

  const totalRealized = trades.filter(t => t.side === "sell").reduce((s, t) => s + (t.realized_pl || 0), 0);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="history-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Terminal / History</div>
          <h1 className="heading text-4xl font-black tracking-tighter mt-2">Trade history</h1>
          <div className={`mono text-sm mt-2 ${totalRealized >= 0 ? "profit" : "loss"}`}>
            Total realized P/L: {fmtMoney(totalRealized, { showSign: true })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => download(method)}
            disabled={trades.length === 0}
            data-testid="export-csv-btn"
            className="bg-white text-black hover:bg-zinc-200 rounded-sm h-10 mono text-[10px] uppercase tracking-widest gap-1.5 disabled:opacity-40"
          >
            <DownloadSimple size={14} weight="bold" /> Export CSV · {method.toUpperCase()}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                disabled={trades.length === 0}
                data-testid="cost-basis-toggle"
                title="Choose cost-basis method"
                className="bg-transparent border-zinc-700 hover:bg-zinc-900 rounded-sm h-10 mono text-[10px] uppercase tracking-widest gap-1.5 disabled:opacity-40"
              >
                Method <CaretDown size={11} weight="bold" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-zinc-950 border-zinc-800 min-w-[240px]"
              data-testid="cost-basis-menu"
            >
              <DropdownMenuLabel className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                Cost-basis method
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-zinc-800" />
              {METHODS.map((m) => (
                <DropdownMenuItem
                  key={m.value}
                  onClick={() => download(m.value)}
                  data-testid={`cost-basis-option-${m.value}`}
                  className="focus:bg-zinc-900 cursor-pointer flex-col items-start gap-0.5"
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="mono text-sm font-bold text-white">{m.label}</span>
                    {method === m.value && (
                      <span className="mono text-[9px] text-[#00C805] uppercase tracking-widest">Current</span>
                    )}
                  </div>
                  <span className="text-[10px] text-zinc-500">{m.desc}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <Card>
        <CardHeader title={`${trades.length} trade${trades.length === 1 ? "" : "s"}`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] mono uppercase tracking-widest text-zinc-500">
              <tr className="border-b border-zinc-800">
                <th className="text-left px-5 py-2.5">Time</th>
                <th className="text-left px-3 py-2.5">Symbol</th>
                <th className="text-left px-3 py-2.5">Side</th>
                <th className="text-right px-3 py-2.5">Qty</th>
                <th className="text-right px-3 py-2.5">Price</th>
                <th className="text-right px-3 py-2.5">Total</th>
                <th className="text-right px-3 py-2.5">Realized P/L</th>
                <th className="text-left px-3 py-2.5">Type</th>
                <th className="text-left px-5 py-2.5">Source</th>
              </tr>
            </thead>
            <tbody className="mono">
              {trades.map(t => (
                <tr key={t.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`trade-row-${t.id}`}>
                  <td className="px-5 py-3 text-zinc-400 text-xs">{new Date(t.executed_at).toLocaleString()}</td>
                  <td className="px-3 py-3 font-semibold">{t.symbol}</td>
                  <td className={`px-3 py-3 uppercase font-bold ${t.side === "buy" ? "profit" : "loss"}`}>{t.side}</td>
                  <td className="text-right px-3 py-3">{t.quantity}</td>
                  <td className="text-right px-3 py-3">{fmtMoney(t.price)}</td>
                  <td className="text-right px-3 py-3">{fmtMoney(t.total)}</td>
                  <td className={`text-right px-3 py-3 ${(t.realized_pl ?? 0) > 0 ? "profit" : (t.realized_pl ?? 0) < 0 ? "loss" : "text-zinc-600"}`}>
                    {t.side === "sell" && t.realized_pl != null ? fmtMoney(t.realized_pl, { showSign: true }) : "—"}
                  </td>
                  <td className="px-3 py-3 text-xs uppercase text-zinc-400">{t.order_type}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${
                      t.source === "bot" ? "border-[#007AFF] text-[#007AFF]" : "border-zinc-700 text-zinc-400"
                    }`}>{t.source}</span>
                  </td>
                </tr>
              ))}
              {trades.length === 0 && (
                <tr><td colSpan={9} className="px-5 py-16 text-center mono text-xs text-zinc-600">No trades yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
